"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { KeyRound } from "lucide-react";
import { listClientOptions } from "@/app/actions/admin/client-actions";
import { ConnectCopilotDialog } from "@/components/api-keys/connect-copilot-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
interface ClientOption {
  id: string;
  name: string;
}
interface ConnectCopilotAdminCtaProps {
  /**
   * The viewer's own organization (enterprise admins — auto-scoped, one
   * click), or null for super admins, who pick the organization the key
   * belongs to. The page only renders this component for viewers who can
   * mint; the server actions behind the dialog re-check authorization on
   * every call regardless (createClientApiKey → requireEnterpriseAdminFor).
   */
  scopedClientId: string | null;
}

/**
 * Admin CTA on /connect/copilot (AIR-704): the single entry point for
 * minting the Copilot key (whole organization or one department), folded into the shareable
 * instructions page after the button moved off the admin API Keys card.
 * Reuses ConnectCopilotDialog unchanged — mint + live gateway self-test +
 * first-traffic poll is the flow admins rely on.
 */
export function ConnectCopilotAdminCta(t0) {
  const $ = _c(25);
  const {
    scopedClientId
  } = t0;
  const needsPicker = scopedClientId === null;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState("");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [clients, setClients] = useState(t1);
  const [clientsLoading, setClientsLoading] = useState(needsPicker);
  const [clientsError, setClientsError] = useState(null);
  let t2;
  let t3;
  if ($[1] !== needsPicker) {
    t2 = () => {
      if (!needsPicker) {
        return;
      }
      let cancelled = false;
      const loadClients = async function loadClients() {
        const result = await listClientOptions();
        if (cancelled) {
          return;
        }
        if (result.success) {
          setClients(result.data);
        } else {
          setClientsError(result.error);
        }
        setClientsLoading(false);
      };
      loadClients();
      return () => {
        cancelled = true;
      };
    };
    t3 = [needsPicker];
    $[1] = needsPicker;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  const clientId = needsPicker ? selectedClientId : scopedClientId;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><KeyRound className="h-4 w-4" />Create a Copilot key</CardTitle></CardHeader>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <p className="text-sm text-muted-foreground">You're signed in as an administrator, so the key can be created right here, for the whole organization or for one department at a time (spend lands on that team's budget). It's verified with a live request through the gateway and shown once — then paste it into GitHub using the steps below.</p>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== clients || $[7] !== clientsError || $[8] !== clientsLoading || $[9] !== needsPicker || $[10] !== selectedClientId) {
    t6 = needsPicker ? <div className="grid gap-1.5"><Label htmlFor="copilot-cta-client">Organization</Label><Select value={selectedClientId} onValueChange={setSelectedClientId} disabled={clientsLoading || clients.length === 0}><SelectTrigger id="copilot-cta-client" className="w-full sm:max-w-sm"><SelectValue placeholder={clientsLoading ? "Loading organizations\u2026" : "Select the organization"} /></SelectTrigger><SelectContent>{clients.map(_temp)}</SelectContent></Select>{clientsError ? <p className="text-sm text-destructive">{clientsError}</p> : null}</div> : null;
    $[6] = clients;
    $[7] = clientsError;
    $[8] = clientsLoading;
    $[9] = needsPicker;
    $[10] = selectedClientId;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setDialogOpen(true);
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = !clientId;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <KeyRound className="mr-2 h-4 w-4" />;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t8) {
    t10 = <Button onClick={t7} disabled={t8}>{t9}Create Copilot key</Button>;
    $[14] = t8;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t6) {
    t11 = <Card className="border-primary/25">{t4}<CardContent className="space-y-3">{t5}{t6}{t10}</CardContent></Card>;
    $[16] = t10;
    $[17] = t6;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== clientId || $[20] !== dialogOpen) {
    t12 = clientId ? <ConnectCopilotDialog clientId={clientId} open={dialogOpen} onOpenChange={setDialogOpen} /> : null;
    $[19] = clientId;
    $[20] = dialogOpen;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t11 || $[23] !== t12) {
    t13 = <>{t11}{t12}</>;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  return t13;
}
function _temp(c) {
  return <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>;
}